import Footer from "../../components/common/layout/Footer";
import FAQ from "../../components/common/sections/FAQ";
import Header from "../../components/common/layout/Header";
import ProductPlansDisplay from "@/components/features/shared/ProductPlansDisplay/ProductPlansDisplay";
import AnimatedSection from "../../components/common/animations/AnimatedSection";
import ParallaxSection from "../../components/common/animations/ParallaxSection";
import Floating3D from "../../components/common/animations/Floating3D";
import { useEffect, useState } from "react";
import { gsap } from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import { motion } from "framer-motion";
import { Check, Server, Zap, Shield, Database, Terminal, AlertTriangle } from "lucide-react";
import { getLinuxHostingPlans } from "@/services/dreamscapeApi";
import { CurrencyCode } from "@/utils/currencyConverter";

if (typeof window !== 'undefined') {
    gsap.registerPlugin(ScrollTrigger);
}

export default function LinuxHosting() {
    const [plans, setPlans] = useState<any[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [selectedCurrency, setSelectedCurrency] = useState<CurrencyCode>('USD');
    const [isDummyData, setIsDummyData] = useState(false);

    const linuxServerFAQs = [
        {
            question: "What is Linux Server?",
            answer: "A Linux Server is a server that uses Linux Operating System (OS). Linux OS is a type of software that controls computer operations and directs program processes. About 70% of web hosting servers use Linux because of affordability, flexibility, and reliability."
        },
        {
            question: "What is a Virtual Server?",
            answer: "A Virtual Server is a hosting service that shares its resources with multiple users within one physical server. Each user can access their own virtual space using their own root access."
        },
        {
            question: "What is the difference between Shared Hosting and Virtual Server?",
            answer: "A Shared Hosting limits your customisation options because it's a pre-configured package. On the other hand, a Virtual Server allows you to customise resources based on the features before deployment."
        },
        {
            question: "What is cPanel?",
            answer: "cPanel is a server management software based on Linux graphical user interface (GUI). Its GUI simplifies the management of web hosting using a browser. Below are some features cPanel can do for you:<br /><br />• Install Content Management Systems like WordPress, Joomla, and many more<br />• Add new domains and subdomains to your hosting account<br />• Upload file directly to a server<br />• Manage FTP account and file uploads<br />• Create and manage databases"
        },
        {
            question: "How soon will my virtual server be ready?",
            answer: "Virtual Servers are usually ready within 24-48 hours. However, it may take longer for some because of its features."
        },
        {
            question: "How do I know which package is best for me?",
            answer: "Since Virtual Servers are flexible with their features, no set package can be considered best. However, you can talk to your developer or system administrator about what you'll need for your web hosting.<br /><br />You may also contact us to discuss the best option for your business."
        },
        {
            question: "Where are the servers located?",
            answer: "Our Virtual Servers are in one of our high-performance vSphere servers in London and Perth as High Available cluster. Connecting High Available (HA) cluster – independent computers that help increase applications and services availability – with physical cables and software ensures a failover that minimises service disruptions."
        },
        {
            question: "Are backups included in the plans?",
            answer: "We generally keep our own backups. However, we strongly encourage you to keep your own.<br /><br />You can create daily, weekly, or monthly backups depending on your preference. Doing so assures you that your backup files are recent.<br /><br />We suggest automating your backup for convenience."
        },
        {
            question: "Do I get a dedicated IP address?",
            answer: "Yes. You get one dedicated IP for your Virtual Server with the option to add more."
        }
    ];

    useEffect(() => {
        // Load Linux hosting plans from API
        const loadPlans = async () => {
            setLoading(true);
            setError(null);
            setIsDummyData(false);
            
            try {
                // Try to fetch real data from Dreamscape API (sandomains.com)
                const result = await getLinuxHostingPlans({ limit: 50 });
                
                if (result.success && result.plans && result.plans.length > 0) {
                    setPlans(result.plans);
                    setIsDummyData(false);
                } else {
                    // If API returns no plans, fall back to dummy data
                    console.warn('No plans returned from API, using dummy data:', result.message);
                    setIsDummyData(true);
                    loadDummyPlans();
                }
            } catch (err) {
                // If API call fails, fall back to dummy data
                console.error('Failed to load plans from API, using dummy data:', err);
                setError(err instanceof Error ? err.message : 'Failed to load plans');
                setIsDummyData(true);
                loadDummyPlans();
            } finally {
                setLoading(false);
            }
        };

        // Fallback dummy data function
        const loadDummyPlans = () => {
            const dummyPlans = [
                {
                    id: 1,
                    name: "Linux Starter",
                    description: "Perfect for small websites and blogs. Get started with Linux hosting at an affordable price.",
                    periods: [
                        { period: 1, price: { register: 3.99, renew: 5.99 } },
                        { period: 12, price: { register: 2.99, renew: 4.99 } },
                        { period: 24, price: { register: 2.49, renew: 4.49 } },
                        { period: 36, price: { register: 1.99, renew: 3.99 } }
                    ],
                    features: [
                        { name: "Disk Space", value: "10 GB" },
                        { name: "Bandwidth", value: "Unlimited" },
                        { name: "Email Accounts", value: "5" },
                        { name: "MySQL Databases", value: "5" },
                        { name: "cPanel Control Panel", value: "Yes" }
                    ]
                },
                {
                    id: 2,
                    name: "Linux Business",
                    description: "Ideal for growing businesses. More resources and features for your Linux-based applications.",
                    periods: [
                        { period: 1, price: { register: 7.99, renew: 11.99 } },
                        { period: 12, price: { register: 6.99, renew: 10.99 } },
                        { period: 24, price: { register: 5.99, renew: 9.99 } },
                        { period: 36, price: { register: 4.99, renew: 8.99 } }
                    ],
                    features: [
                        { name: "Disk Space", value: "50 GB" },
                        { name: "Bandwidth", value: "Unlimited" },
                        { name: "Email Accounts", value: "Unlimited" },
                        { name: "MySQL Databases", value: "Unlimited" },
                        { name: "cPanel Control Panel", value: "Yes" },
                        { name: "PHP Versions", value: "Multiple" }
                    ]
                },
                {
                    id: 3,
                    name: "Linux Professional",
                    description: "For professional websites and applications. Maximum performance and resources.",
                    periods: [
                        { period: 1, price: { register: 12.99, renew: 17.99 } },
                        { period: 12, price: { register: 11.99, renew: 16.99 } },
                        { period: 24, price: { register: 10.99, renew: 15.99 } },
                        { period: 36, price: { register: 9.99, renew: 14.99 } }
                    ],
                    features: [
                        { name: "Disk Space", value: "Unlimited" },
                        { name: "Bandwidth", value: "Unlimited" },
                        { name: "Email Accounts", value: "Unlimited" },
                        { name: "MySQL Databases", value: "Unlimited" },
                        { name: "cPanel Control Panel", value: "Yes" },
                        { name: "PHP Versions", value: "Multiple" },
                        { name: "Dedicated IP", value: "1" }
                    ]
                }
            ];
            setPlans(dummyPlans);
        };

        loadPlans();
    }, []);

    useEffect(() => {
        // Animate sections on scroll
        const sections = document.querySelectorAll('.hosting-section');

        sections.forEach((section, index) => {
            gsap.fromTo(section,
                {
                    opacity: 0,
                    y: 60,
                    scale: 0.95
                },
                {
                    opacity: 1,
                    y: 0,
                    scale: 1,
                    duration: 0.8,
                    delay: index * 0.15,
                    ease: "power3.out",
                    scrollTrigger: {
                        trigger: section,
                        start: "top 80%",
                        toggleActions: "play none none none"
                    }
                }
            );
        });

        return () => {
            ScrollTrigger.getAll().forEach(trigger => trigger.kill());
        };
    }, [plans]);

    const handleSelectPlan = (plan: any, period: number, price: number) => {
        console.log('Selected Linux hosting plan:', plan, 'Period:', period, 'Price:', price);
        // TODO: Implement cart/checkout flow
    };

    const features = [
        {
            icon: Terminal,
            title: "cPanel Control",
            description: "Easy-to-use cPanel control panel for managing your hosting account."
        },
        {
            icon: Database,
            title: "MySQL Database",
            description: "Unlimited MySQL databases for your applications and websites."
        },
        {
            icon: Server,
            title: "PHP Support",
            description: "Latest PHP versions with full support for all PHP frameworks."
        },
        {
            icon: Shield,
            title: "SSL Certificates",
            description: "Free SSL certificates to secure your website and boost SEO."
        },
        {
            icon: Zap,
            title: "Fast SSD Storage",
            description: "Lightning-fast SSD storage for optimal website performance."
        },
        {
            icon: Check,
            title: "24/7 Support",
            description: "Expert Linux hosting support available around the clock."
        }
    ];

    return (
        <div className="relative overflow-hidden min-h-screen text-white">
            <Header />

            {/* Hero Section */}
            <ParallaxSection speed={0.25}>
                <Floating3D intensity={18}>
                    <section className="hosting-section container py-20 pt-32">
                        <div className="text-center max-w-4xl mx-auto">
                            <h1 className="text-5xl md:text-6xl font-bold mb-6 text-transparent bg-clip-text bg-gradient-to-r from-brandStart to-brandEnd">
                                Linux Web Hosting
                            </h1>
                            <p className="text-xl text-textColor mb-8 leading-relaxed">
                                Get the best value for your Linux web-hosting with all the latest features
                                you need on your website runtime and speed first.
                            </p>
                        </div>
                    </section>
                </Floating3D>
            </ParallaxSection>

            {/* Pricing Plans */}
            <AnimatedSection delay={0.2} direction="up">
                <div className="hosting-section">
                    {isDummyData ? (
                        <div className="container py-12">
                            <div className="max-w-2xl mx-auto">
                                <div className="bg-yellow-500/10 border-2 border-yellow-500/50 rounded-lg p-6 flex items-start gap-4">
                                    <AlertTriangle className="w-6 h-6 text-yellow-500 flex-shrink-0 mt-0.5" />
                                    <div className="flex-1">
                                        <h3 className="text-yellow-400 font-bold text-lg mb-2">
                                            Using Fallback Data
                                        </h3>
                                        <p className="text-yellow-300/90 text-sm leading-relaxed">
                                            Unable to load plans from the API. Displaying sample data. Please check your API configuration or contact support to ensure real hosting plan data is available from sandomains.com.
                                        </p>
                                    </div>
                                </div>
                            </div>
                        </div>
                    ) : null}
                    <ProductPlansDisplay
                        plans={plans}
                        loading={loading}
                        error={error}
                        currency={selectedCurrency}
                        onCurrencyChange={setSelectedCurrency}
                        onSelectPlan={handleSelectPlan}
                        buttonText="Get Started"
                        priceLabel="per month"
                    />
                </div>
            </AnimatedSection>

            {/* Features Section */}
            <ParallaxSection speed={0.3} direction="down">
                <AnimatedSection delay={0.3} direction="left">
                    <section className="hosting-section container py-20">
                        <div className="text-center mb-16">
                            <h2 className="text-4xl font-bold mb-4">Why Choose Linux Hosting?</h2>
                            <p className="text-textColor text-lg">Reliable, affordable, and feature-rich hosting</p>
                        </div>
                        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
                            {features.map((feature, index) => {
                                const Icon = feature.icon;
                                return (
                                    <motion.div
                                        key={index}
                                        className="bg-cardBg/90 backdrop-blur-xl rounded-2xl p-6 border border-white/10 hover:border-primary/30 transition-all"
                                        whileHover={{ scale: 1.02, y: -5 }}
                                        transition={{ type: "spring", stiffness: 300 }}
                                    >
                                        <div className="w-12 h-12 rounded-full bg-gradient-to-r from-brandStart to-brandEnd flex items-center justify-center mb-4">
                                            <Icon className="w-6 h-6 text-white" />
                                        </div>
                                        <h3 className="text-xl font-bold mb-2">{feature.title}</h3>
                                        <p className="text-textColor">{feature.description}</p>
                                    </motion.div>
                                );
                            })}
                        </div>
                    </section>
                </AnimatedSection>
            </ParallaxSection>

            {/* FAQ Section */}
            <AnimatedSection delay={0.6}>
                <div className="abstract-bg hosting-section">
                    <FAQ faqs={linuxServerFAQs} />
                </div>
            </AnimatedSection>

            <AnimatedSection delay={0.7}>
                <Footer />
            </AnimatedSection>
        </div>
    );
}

